import { graphql, usePaginationFragment } from "react-relay";
import { FileBrowserFragment$key } from "./__generated__/FileBrowserFragment.graphql";
import { FormattedNumber, FormattedMessage } from "react-intl";
import { useNavigate } from "react-router";
import FileTypeIcon from "./FileTypeIcon";
import PathBreadcrumbs from "./PathBreadcrumbs";
import { Path, joinPath } from "../../utils/url";
import { Button } from "@/kit/ui/button";
import { Home02Icon } from "@/icons";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/kit/ui/table";

const FileBrowserFragment = graphql`
  fragment FileBrowserFragment on ProjectVersionFile
  @refetchable(queryName: "FileBrowserPaginationQuery")
  @argumentDefinitions(
    path: { type: "String" }
    cursor: { type: "String" }
    count: { type: "Int", defaultValue: 100 }
  ) {
    browse {
      readDir(path: $path, after: $cursor, first: $count)
        @connection(key: "FileBrowser_readDir") {
        edges {
          node {
            name
            kind
            ... on FileBrowserFileEntry {
              contentType
              contentLength
            }
          }
        }
      }
    }
  }
`;

interface Props {
  root: Path;
  path?: string;
  file: FileBrowserFragment$key;
}

const uniformPath = (path: string) => {
  if (path.startsWith("/")) {
    path = path.slice(1);
  }
  if (path.length > 0 && !path.endsWith("/")) {
    path = path + "/";
  }
  return path;
};

export default function FileBrowser({
  file: fileFragment,
  path: nonUniformPath,
  root,
}: Props) {
  const { data, loadNext, hasNext } = usePaginationFragment(
    FileBrowserFragment,
    fileFragment,
  );
  const navigate = useNavigate();
  const path = uniformPath(nonUniformPath || "");
  return (
    <div className="flex flex-col space-y-4">
      <div className="flex flex-row items-center">
        <Button
          className="cursor-pointer"
          disabled={path.length < 1}
          variant="tertiary"
          onClick={() => {
            navigate(-1);
          }}
          icon
        >
          <Home02Icon />
        </Button>

        <PathBreadcrumbs root={root} path={path} />
      </div>

      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead colSpan={2}>
                <FormattedMessage defaultMessage="Name" />
              </TableHead>
              <TableHead>
                <FormattedMessage defaultMessage="Type" />
              </TableHead>
              <TableHead>
                <FormattedMessage defaultMessage="Size" />
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.browse.readDir.edges.map(({ node: entry }) => (
              <TableRow
                key={entry.name}
                className="cursor-pointer text-nowrap"
                onClick={() => {
                  if (entry.kind === "FILE") {
                    navigate(joinPath(root, `blob/${path}${entry.name}`));
                  } else {
                    navigate(joinPath(root, `tree/${path}${entry.name}`));
                  }
                }}
              >
                <TableCell className="text-grey-700 ps-4 py-2">
                  <FileTypeIcon fileType={entry.kind} />
                </TableCell>
                <TableCell className="text-brand-600 ps-2 pe-4 py-2 w-full">
                  {entry.name}
                </TableCell>
                <TableCell className="text-grey-500 ps-2 pe-4 py-2">
                  {entry.kind === "FILE" && entry.contentType}
                </TableCell>
                <TableCell className="px-4">
                  {entry.contentLength && (
                    <FormattedNumber
                      value={entry.contentLength}
                      notation="compact"
                      style="unit"
                      unit="byte"
                      unitDisplay="narrow"
                    />
                  )}
                </TableCell>
              </TableRow>
            ))}
            {hasNext && (
              <TableRow>
                <TableCell colSpan={4} className="text-center">
                  <Button variant="tertiary" onClick={() => loadNext(100)}>
                    <FormattedMessage defaultMessage="Load more" />
                  </Button>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
